/**
 * Real-time ticket feed (operator console). Loads the initial page over REST,
 * then keeps it live via the Socket.IO channel (new tickets prepend, updates
 * replace in place). A status filter narrows the list; a search box filters the
 * loaded page client-side; a connection dot shows the socket state.
 *
 * Visually this is the Claude Design "ticket feed": a toolbar (search · status
 * segments · live indicator) over a dense, sticky-headed table of ticket rows.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import {
  TICKET_STATUSES,
  type Ticket,
  type TicketStatus,
  type TicketListResponse,
} from '@hotline/contracts';

import { useAuth } from '../../auth/AuthContext.js';
import { useOrgScope } from '../../org/OrgScopeContext.js';
import { useTicketFeed } from '../../realtime/useRealtime.js';
import { useAsync } from '../../lib/useAsync.js';
import {
  Alert,
  Avatar,
  EmptyState,
  Input,
  QualityScore,
  Spinner,
  StatusPill,
  cn,
} from '../../components/ui/index.js';
import { Icon } from '../../components/ui/icons.js';

/** Grid template shared by the sticky head and every row (design `--cols`). */
const COLS =
  'grid-cols-[1fr_116px_minmax(120px,150px)_minmax(120px,160px)_78px_64px]';

/** Short relative age, e.g. "12s" / "4m" / "3h" / "2d" (design `t.age`). */
function age(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/** Map a quality score to the design's bar tone (≥.85 ok · ≥.7 warn · else info). */
function qualityTone(v: number): 'ok' | 'warn' | 'info' {
  if (v >= 0.85) return 'ok';
  if (v >= 0.7) return 'warn';
  return 'info';
}

/** Host of a page URL for the compact "Source" cell, falling back gracefully. */
function sourceLabel(t: Ticket): string {
  if (t.pageTitle) return t.pageTitle;
  if (t.pageUrl) {
    try {
      return new URL(t.pageUrl).hostname.replace(/^www\./, '');
    } catch {
      return t.pageUrl;
    }
  }
  return 'Unknown';
}

/** Reporter display name + initials for the avatar (anonymous when no email). */
function reporter(t: Ticket): { name: string; initials: string } {
  const email = t.endUserEmail?.trim();
  if (!email) return { name: 'Anonymous', initials: 'A' };
  const handle = email.split('@')[0] ?? email;
  return { name: email, initials: handle.slice(0, 2).toUpperCase() };
}

export default function TicketFeedPage(): ReactElement {
  const { client } = useAuth();
  const { query, orgId } = useOrgScope();
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [search, setSearch] = useState('');

  const initial = useAsync<TicketListResponse>(
    () =>
      client.api.listTickets(
        { status: statusFilter || undefined, limit: 50 },
        query,
      ),
    [client, orgId, statusFilter],
  );

  const feed = useTicketFeed(orgId);

  // Seed / re-seed the live list whenever the REST page (re)loads.
  useEffect(() => {
    if (initial.data) feed.setTickets(initial.data.items);
    // feed.setTickets is stable; intentionally only react to new data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.data]);

  const visible = useMemo(() => {
    let list = statusFilter
      ? feed.tickets.filter((t) => t.status === statusFilter)
      : feed.tickets;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((t) =>
        [t.aiTitle, t.aiSummary, t.aiCategory, t.pageUrl, t.pageTitle, t.id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q),
      );
    }
    return list;
  }, [feed.tickets, statusFilter, search]);

  const clearFilters = (): void => {
    setStatusFilter('');
    setSearch('');
  };

  return (
    <section className="flex flex-col gap-4">
      {/* ── Toolbar: search · status segments · live indicator ───────────── */}
      <div
        role="toolbar"
        aria-label="Ticket feed controls"
        className="flex flex-wrap items-center gap-2.5"
      >
        <div className="relative max-w-[360px] flex-1">
          <span className="pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2 text-faint">
            <Icon.search size={15} />
          </span>
          <Input
            type="search"
            aria-label="Search tickets"
            placeholder="Search tickets, errors, pages…"
            className="pl-[33px]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div
          role="radiogroup"
          aria-label="Filter by status"
          className="inline-flex gap-0.5 overflow-x-auto rounded-md border border-border bg-surface-2 p-[3px]"
        >
          <button
            type="button"
            role="radio"
            aria-checked={statusFilter === ''}
            data-state={statusFilter === '' ? 'on' : 'off'}
            onClick={() => setStatusFilter('')}
            className={cn(
              'whitespace-nowrap rounded-sm px-[11px] py-[5px] text-[12.5px] font-semibold capitalize transition',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
              statusFilter === ''
                ? 'bg-surface-3 text-text shadow-sm'
                : 'text-muted hover:text-text',
            )}
          >
            All
          </button>
          {TICKET_STATUSES.map((s) => {
            const selected = statusFilter === s;
            return (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={selected}
                data-state={selected ? 'on' : 'off'}
                onClick={() => setStatusFilter(s)}
                className={cn(
                  'whitespace-nowrap rounded-sm px-[11px] py-[5px] text-[12.5px] font-semibold capitalize transition',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                  selected
                    ? 'bg-surface-3 text-text shadow-sm'
                    : 'text-muted hover:text-text',
                )}
              >
                {s.replace('_', ' ')}
              </button>
            );
          })}
        </div>

        <span className="ml-auto flex items-center gap-[7px] text-xs font-bold text-muted">
          <span
            className={cn(
              'h-[7px] w-[7px] rounded-full',
              feed.connected
                ? 'bg-ok shadow-[0_0_7px_var(--hl-ok)]'
                : 'bg-faint',
            )}
            title={feed.connected ? 'Live' : 'Disconnected'}
            aria-label={
              feed.connected ? 'Realtime connected' : 'Realtime disconnected'
            }
          />
          <span aria-hidden="true">{feed.connected ? 'Live' : 'Offline'}</span>
        </span>
      </div>

      {initial.error ? <Alert>{initial.error}</Alert> : null}

      {/* ── Dense ticket table ───────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-xl border border-border bg-surface">
        {/* sticky column header */}
        <div
          className={cn(
            'sticky top-0 z-[5] grid items-center gap-[14px] border-b border-border px-5 py-[9px]',
            'bg-bg/90 text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-faint backdrop-blur',
            COLS,
          )}
        >
          <div className="flex items-center gap-[5px]">
            Ticket <Icon.chevDown size={12} className="opacity-60" />
          </div>
          <div>Status</div>
          <div>Source</div>
          <div>Reporter</div>
          <div>Quality</div>
          <div className="justify-self-end">Age</div>
        </div>

        {initial.loading ? (
          <div className="px-5 py-8">
            <Spinner label="Loading tickets…" />
          </div>
        ) : null}

        {!initial.loading && visible.length === 0 ? (
          search || statusFilter ? (
            <EmptyState
              icon={<Icon.search />}
              title="No matches"
              description="Nothing matches your search and filters right now."
              action={
                <button
                  type="button"
                  onClick={clearFilters}
                  className="rounded-md px-3 py-1.5 text-[13px] font-semibold text-muted transition hover:bg-surface-hover hover:text-text"
                >
                  Clear filters
                </button>
              }
            />
          ) : (
            <EmptyState
              icon={<Icon.inbox />}
              title="No tickets yet"
              description="Feedback will appear here in real time."
            />
          )
        ) : null}

        <ul>
          {visible.map((t, i) => {
            const rep = reporter(t);
            const title =
              t.aiTitle || t.aiSummary || `Ticket ${t.id.slice(0, 8)}`;
            return (
              <li
                key={t.id}
                className={cn(
                  'ticket-row border-b border-border last:border-b-0',
                  i === 0 && 'animate-in fade-in slide-in-from-top-1 duration-300',
                )}
                data-ticket-id={t.id}
              >
                <Link
                  to={`/org/tickets/${t.id}`}
                  className={cn(
                    'grid min-h-[52px] items-center gap-[14px] px-5 py-3 transition-colors',
                    'hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
                    COLS,
                  )}
                >
                  {/* Ticket — title + meta sub-line */}
                  <div className="min-w-0">
                    <div className="truncate text-[13.5px] font-semibold text-text">
                      {title}
                    </div>
                    <div className="mt-px flex items-center gap-[7px] truncate text-xs text-muted">
                      <span className="mono text-[11px] text-faint">
                        {t.id.slice(0, 10)}
                      </span>
                      {t.aiCategory ? (
                        <>
                          <span aria-hidden="true">·</span>
                          <span className="truncate">{t.aiCategory}</span>
                        </>
                      ) : null}
                      {t.channel === 'voice' ? (
                        <>
                          <span aria-hidden="true">·</span>
                          <span className="inline-flex items-center gap-1">
                            <Icon.mic size={11} />
                            voice
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>

                  {/* Status */}
                  <div className="min-w-0">
                    <StatusPill status={t.status} />
                  </div>

                  {/* Source */}
                  <div
                    className="flex min-w-0 items-center gap-[7px] text-xs font-medium text-muted"
                    title={t.pageUrl ?? undefined}
                  >
                    <span className="grid h-[18px] w-[18px] flex-shrink-0 place-items-center rounded-[5px] bg-primary-soft text-[9px] font-extrabold text-primary">
                      {sourceLabel(t).slice(0, 1).toUpperCase()}
                    </span>
                    <span className="truncate">{sourceLabel(t)}</span>
                  </div>

                  {/* Reporter */}
                  <div className="flex min-w-0 items-center gap-2">
                    <Avatar size="sm" fallback={rep.initials} />
                    <span className="truncate text-[12.5px] text-text">
                      {rep.name}
                    </span>
                  </div>

                  {/* Quality */}
                  <div className="min-w-0">
                    {typeof t.qualityScore === 'number' ? (
                      <QualityScore
                        value={t.qualityScore}
                        tone={qualityTone(t.qualityScore)}
                      />
                    ) : (
                      <span className="text-[11.5px] text-faint">—</span>
                    )}
                  </div>

                  {/* Age */}
                  <time
                    dateTime={t.createdAt}
                    title={new Date(t.createdAt).toLocaleString()}
                    className="mono justify-self-end text-[11.5px] tabular-nums text-faint"
                  >
                    {age(t.createdAt)}
                  </time>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
